import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { Target as TargetIcon, X, CheckCircle2 } from "lucide-react";

/**
 * TourPickerOverlay — element picker for the Walkthrough admin editor.
 *
 * Mounted in AppShell. Activates ONLY when the URL contains
 * `?_tourPicker=<stepId>` — otherwise renders nothing.
 *
 * When active:
 *   1. Follow the mouse; highlight the hovered element with a colored outline
 *      + label showing the selector we'd capture.
 *   2. Prefer `[data-testid='…']` over id/class chains.
 *   3. On click, POST the selector back to the opener tab via BroadcastChannel
 *      (and localStorage as a fallback for browsers without it), then close
 *      the tab.
 *
 * Cross-tab handshake key must match `PICKER_CHANNEL` in AdminContentConsoles.jsx.
 */
const PICKER_CHANNEL = "cpg_tour_picker";

/**
 * Build a selector that matches the EXACT element the admin clicked —
 * never a distant ancestor. Walking up to a testid-bearing container
 * (previous behavior) was capturing `[data-testid=app-content-frame]`
 * whenever the click target was inside a card that had no testid of
 * its own, so the walkthrough highlight ended up wrapping the entire
 * content area instead of the specific card.
 *
 * Priority ladder — first hit wins, top→down:
 *   1. Clicked element's own `data-testid`
 *   2. Clicked element's own `id`  → `tag#id`
 *   3. Immediate parent's `data-testid` scoped to the clicked child:
 *      `[data-testid='parent'] > tag:nth-of-type(N)`
 *   4. Clicked element's tag + one class, plus `:nth-of-type(N)`
 *      when needed for specificity.
 *
 * This guarantees the runtime highlight lands on the clicked pixel
 * region, not on a wrapper hundreds of pixels wider.
 */
function selectorFor(el) {
  if (!el || el === document.documentElement || el === document.body) return "body";

  // (1) direct testid on the clicked element
  const testid = el.getAttribute?.("data-testid");
  if (testid) return `[data-testid='${testid}']`;

  // (2) direct id
  const tag = (el.tagName || "*").toLowerCase();
  if (el.id) return `${tag}#${CSS.escape ? CSS.escape(el.id) : el.id}`;

  // Helper — nth-of-type index of a child among same-tag siblings.
  const nthOfType = (node) => {
    if (!node || !node.parentElement) return 1;
    let i = 1;
    for (const sib of node.parentElement.children) {
      if (sib === node) return i;
      if (sib.tagName === node.tagName) i += 1;
    }
    return i;
  };

  // (3) parent (or grandparent) testid → scope to the clicked child.
  //     Cap at 2 levels so we never accidentally scope to a huge
  //     container like `app-content-frame` while still handling
  //     the common "card inside a testid'd list" pattern.
  let node = el.parentElement;
  let hop = 0;
  let chain = [el];
  while (node && node !== document.body && hop < 2) {
    const t = node.getAttribute?.("data-testid");
    if (t) {
      // Build `[data-testid='X'] > tag:nth-of-type(N) > tag:nth-of-type(M) ...`
      const path = chain.slice().reverse().map((n) => {
        const nt = (n.tagName || "*").toLowerCase();
        return `${nt}:nth-of-type(${nthOfType(n)})`;
      }).join(" > ");
      return `[data-testid='${t}'] > ${path}`;
    }
    chain.push(node);
    node = node.parentElement;
    hop += 1;
  }

  // (4) class-based fallback on the clicked element itself.
  const classes = (el.className && typeof el.className === "string")
    ? el.className.split(/\s+/).filter(Boolean).slice(0, 2)
    : [];
  const classPart = classes.length ? "." + classes.map((c) => CSS.escape ? CSS.escape(c) : c).join(".") : "";
  const idx = nthOfType(el);
  return `${tag}${classPart}:nth-of-type(${idx})`;
}

export default function TourPickerOverlay() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const nav = useNavigate();
  const stepId = params.get("_tourPicker");
  const [hoverBox, setHoverBox] = useState(null);
  const [hoverSelector, setHoverSelector] = useState("");
  const [captured, setCaptured] = useState(null);

  useEffect(() => {
    if (!stepId) return;
    const onMove = (e) => {
      // Ignore the overlay itself so it doesn't select its own chrome.
      const overlay = document.getElementById("cpg-tour-picker-overlay");
      if (overlay && overlay.contains(e.target)) return;
      const el = e.target;
      if (!(el instanceof Element)) return;
      const r = el.getBoundingClientRect();
      setHoverBox({ top: r.top, left: r.left, width: r.width, height: r.height });
      setHoverSelector(selectorFor(el));
    };
    const onClick = (e) => {
      const overlay = document.getElementById("cpg-tour-picker-overlay");
      if (overlay && overlay.contains(e.target)) return;
      e.preventDefault(); e.stopPropagation();
      const el = e.target;
      const sel = selectorFor(el);
      setCaptured(sel);
      handOff(sel);
    };
    const onKey = (e) => { if (e.key === "Escape") cancel(); };
    document.addEventListener("mousemove", onMove, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("keydown", onKey);
    document.body.style.cursor = "crosshair";
    return () => {
      document.removeEventListener("mousemove", onMove, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("keydown", onKey);
      document.body.style.cursor = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepId]);

  const handOff = (selector) => {
    // 1. BroadcastChannel — modern & reliable when both tabs are open.
    try {
      const bc = new BroadcastChannel(PICKER_CHANNEL);
      bc.postMessage({ stepId, selector });
      bc.close();
    } catch { /* older browsers */ }
    // 2. localStorage 'storage' event — cross-tab fallback.
    try {
      localStorage.setItem(`${PICKER_CHANNEL}_${stepId}`, JSON.stringify({ selector, at: Date.now() }));
      // Clean up immediately so a subsequent picker for the same step re-fires.
      setTimeout(() => { try { localStorage.removeItem(`${PICKER_CHANNEL}_${stepId}`); } catch {} }, 800);
    } catch {}
    // Give the user a beat to see confirmation, then close (or return if opener missing).
    setTimeout(() => {
      if (window.opener) window.close();
      else {
        const next = new URLSearchParams(location.search);
        next.delete("_tourPicker");
        nav({ pathname: location.pathname, search: next.toString() }, { replace: true });
      }
    }, 700);
  };

  const cancel = () => {
    if (window.opener) window.close();
    else {
      const next = new URLSearchParams(location.search);
      next.delete("_tourPicker");
      nav({ pathname: location.pathname, search: next.toString() }, { replace: true });
    }
  };

  if (!stepId) return null;

  return (
    <div id="cpg-tour-picker-overlay" className="fixed inset-0 z-[10000] pointer-events-none">
      {/* Hover outline */}
      {hoverBox && !captured && (
        <div
          className="absolute border-2 border-primary bg-primary/10 rounded-md transition-all pointer-events-none"
          style={{
            top: hoverBox.top - 2, left: hoverBox.left - 2,
            width: hoverBox.width + 4, height: hoverBox.height + 4,
          }}
        />
      )}
      {/* Captured confirmation */}
      {captured && hoverBox && (
        <div
          className="absolute border-2 border-emerald-500 bg-emerald-500/15 rounded-md pointer-events-none"
          style={{
            top: hoverBox.top - 2, left: hoverBox.left - 2,
            width: hoverBox.width + 4, height: hoverBox.height + 4,
          }}
        />
      )}
      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 bg-shell-side text-white shadow-xl pointer-events-auto" data-testid="tour-picker-bar">
        <div className="max-w-5xl mx-auto flex items-center gap-3 px-4 py-2.5">
          <TargetIcon className="w-4 h-4 text-shell-accent shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-shell-accent">Picking element</p>
            <p className="text-[13px] truncate">
              {captured ? (
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Captured <code className="bg-white/15 px-1.5 py-0.5 rounded ml-1">{captured}</code></span>
              ) : (
                <>Click any element to capture · <code className="bg-white/15 px-1.5 py-0.5 rounded">{hoverSelector || "hover to preview"}</code></>
              )}
            </p>
          </div>
          <button type="button" onClick={cancel} data-testid="tour-picker-cancel"
            className="inline-flex items-center gap-1 rounded-full border border-white/30 px-3 py-1 text-[11px] font-semibold hover:bg-white/10 transition">
            <X className="w-3 h-3" /> Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
